import {OrderDirection} from "common/contracts/order-direction";
import Guid from "common/values/guid/guid";
import Date from "common/values/date/date";
import { ProposalStatus } from "work/values/constants"

export default class ProposalParameters {
  filterParameters: ProposalFilterParameters;
  orderParameters: ProposalOrderParameters;

  constructor(
    filterParameters: ProposalFilterParameters,
    orderParameters: ProposalOrderParameters
  ) {
    this.filterParameters = filterParameters;
    this.orderParameters = orderParameters;
  }

  public asSearchParams(): URLSearchParams {
    return new URLSearchParams([
      ...this.filterParameters.asSearchParams(),
      ...this.orderParameters.asSearchParams(),
    ])
  }
}

export class ProposalFilterParameters {
  query?: string;

  RFPId?: Guid;

  name?: string;

  description?: string;

  clientUserId?: Guid;
  clientEntityId?: Guid;
  clientTeamMemberUserId?: Guid;
  clientTeamMemberEntityId?: Guid;

  hasStart?: boolean;
  minStart?: Date;
  maxStart?: Date;

  hasEnd?: boolean;
  minEnd?: Date;
  maxEnd?: Date;

  vendorTeamRestricted?: boolean;
  vendorUserId?: Guid;
  vendorEntityId?: Guid;
  vendorTeamMemberUserId?: Guid;
  vendorTeamMemberEntityId?: Guid;

  additionalVendorUserId?: Guid;
  additionalVendorEntityId?: Guid;

  feeScheduleCategoryId?: Guid;
  feeScheduleCategoryName?: string;
  feeScheduleCategoryDescription?: string;

  teamTemplateId?: Guid;
  teamTemplateName?: string;
  feeScheduleTemplateId?: Guid;
  feeScheduleTemplateName?: string;

  minDiscount?: number;
  maxDiscount?: number;

  minCreated?: Date;
  maxCreated?: Date;

  isNegotiable?: boolean;

  hasResponseDueBy?: boolean;
  minResponseDueBy?: Date;
  maxResponseDueBy?: Date;

  isSuperseded?: boolean;
  supersededByProposalId?: Guid;

  clientReviewerId?: Guid;
  vendorReviewerId?: Guid;

  status?: ProposalStatus;

  event?: ProposalEvent;
  eventActorUserId?: Guid;
  minEventDate?: Date;
  maxEventDate?: Date;

  boxType?: ProposalBoxType;
  context?: ProposalContext;

  isDeleted?: boolean;
  maxDeleted?: Date;
  minDeleted?: Date;

  userId?: Guid;

  public constructor(params: Partial<ProposalFilterParameters> = {}) {
    Object.assign(this, params);
  }

  public asSearchParams(): URLSearchParams {
    const params = new URLSearchParams();
    Object.entries(this).forEach(([key, value]) => {
      if (value !== undefined) {
        params.append(key, value?.toString() ?? "null");
      }
    });
    return params;
  }

  isEqualTo(other: ProposalFilterParameters): boolean {
    return Object.entries(this)
      .every(([key, value]) => value === other[key as keyof ProposalFilterParameters]);
  }
}

export class ProposalOrderParameters {
  orderBy: ProposalOrderField;
  orderDir: OrderDirection;

  constructor(
    orderBy: ProposalOrderField,
    orderDir: OrderDirection
  ) {
    this.orderBy = orderBy;
    this.orderDir = orderDir;
  }

    public asSearchParams(): URLSearchParams {
      const params = new URLSearchParams();
      params.append(
        "orderBy",
        this.orderBy
      );
      params.append(
        "orderDir",
        this.orderDir
      );
      return params;
  }

  public isEqualTo(other: ProposalOrderParameters): boolean {
    if (!other) return false;
    return this.orderBy === other.orderBy && this.orderDir === other.orderDir;
  }
}

export enum ProposalEvent {
  Drafted = "Drafted",
  SubmittedByCreator = "SubmittedByCreator",
  SubmittedByClientTeamLeader = "SubmittedByClientTeamLeader",
  SubmittedByVendorTeamLeader = "SubmittedByVendorTeamLeader",
  SubmittedByAdditionalVendor = "SubmittedByAdditionalVendor",
  SubmittedByVendorTeamMember = "SubmittedByVendorTeamMember",
  ApprovedByVendorTeamLeader = "ApprovedByVendorTeamLeader",
  ApprovedByAdditionalVendor = "ApprovedByAdditionalVendor",
  ApprovedByVendors = "ApprovedByVendors",
  ApprovedByVendorTeamMember = "ApprovedByVendorTeamMember",
  ApprovedByVendorTeam = "ApprovedByVendorTeam",
  ApprovedByClientTeamLeader = "ApprovedByClientTeamLeader",
  Hired = "Hired",
  SubmittedAsRevision = "SubmittedAsRevision",
  RevisionRequested = "RevisionRequested",
  RevisionRejected = "RevisionRejected",
  Revised = "Revised",
  RejectedByClientTeamLeader = "RejectedByClientTeamLeader",
  RejectedByAdditionalVendor = "RejectedByAdditionalVendor",
  RejectedByVendorTeamLeader = "RejectedByVendorTeamLeader",
  RejectedByVendorTeamMember = "RejectedByVendorTeamMember",
  Canceled = "Canceled",
  Finished = "Finished",
  Deleted = "Deleted"
}

export enum ProposalBoxType {
  Draft = "Draft",
  DraftReview = "DraftReview",
  Inbox = "Inbox",
  Outbox = "Outbox",
  ActiveReview = "ActiveReview",
  AllActive = "AllActive",
  Archive = "Archive"
}

export enum ProposalContext {
  Client = "Client",
  Vendor = "Vendor",
  None = "None"
}

export enum ProposalOrderField {
  Created = "Created",
  Name = "Name",
  Start = "Start",
  End = "End",
  ResponseDueBy = "ResponseDueBy",
  Discount = "Discount",
  Updated = "Updated",
  Deleted = "Deleted"
}
